import { useState, useMemo, useEffect } from 'react';
import { log } from '../utils/protokoll';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { elementeApi, kundenApi, standorteApi, gebaeudeApi, stockwerkeApi, maengelApi, pruefbereicheApi, Pruefbereich, excelApi, reportsApi } from '../services/api';
import { ElementWithDetails, ElementTyp, STATUS_LABELS, TYP_LABELS, schluesselFuerElementTyp } from '../types/elemente';
import {
  Search,
  Filter,
  DoorOpen,
  ShieldCheck,
  MapPin,
  Building2,
  Layers,
  X,
  ChevronRight,
  AlertTriangle,
  Camera,
  Edit2,
  Trash2,
  Eye,
  RefreshCw,
  Download,
  Users,
  QrCode,
  Flame,
  Wind,
  Bell,
  Droplets
} from 'lucide-react';

/** Icon je Elementtyp -- vollständig für alle 6 Bereiche (Phase 6, Lücke 3). */
const ELEMENT_TYP_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  'TÜR': DoorOpen,
  SCHOTT: ShieldCheck,
  FEUERLOESCHER: Flame,
  RWA: Wind,
  BMA: Bell,
  SPRINKLER: Droplets,
};
import QRCodeDisplay from '../components/QRCodeDisplay';
import FotoPanel from '../components/FotoPanel';
import DoorCaptureWizard from '../components/DoorCaptureWizard';
import GenericElementCaptureWizard from '../components/GenericElementCaptureWizard';
interface FilterState {
  kundeId?: number;
  standortId?: number;
  gebaeudeId?: number;
  stockwerkId?: number;
  typ?: ElementTyp;
  status?: string;
  search: string;
}

export default function Uebersicht() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [erzeugeInformationsblatt, setErzeugeInformationsblatt] = useState(false);
  const [filters, setFilters] = useState<FilterState>({ search: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedElement, setSelectedElement] = useState<ElementWithDetails | null>(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Bearbeiten-Overlay (Phase 6, Lücke 1): welcher Wizard aufgeht, hängt
  // vom Prüfbereich des Elements ab -- TÜR hat ein eigenes Formular
  // (DoorCaptureWizard), alle anderen Bereiche das generische.
  const [editTuerElement, setEditTuerElement] = useState<ElementWithDetails | null>(null);
  const [editGenericBereich, setEditGenericBereich] = useState<Pruefbereich | null>(null);
  const [exportLaeuft, setExportLaeuft] = useState(false);
  const [exportFehler, setExportFehler] = useState<string | null>(null);

  // Der Download-Button oben rechts war bislang ein reines No-Op
  // (`{/* TODO: Export */}`, Fund aus dem Code-Review). Nutzt dasselbe
  // Excel-Exportprofil wie ImportExport.tsx statt einen zweiten
  // Exportweg zu bauen.
  const elementeExportieren = async () => {
    setExportLaeuft(true);
    setExportFehler(null);
    try {
      const { blob, dateiname } = await excelApi.export('elemente');
      const url = window.URL.createObjectURL(blob);
      const verweis = document.createElement('a');
      verweis.href = url;
      verweis.download = dateiname;
      document.body.appendChild(verweis);
      verweis.click();
      document.body.removeChild(verweis);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const antwort = (err as { response?: { data?: { error?: string } } })?.response?.data;
      setExportFehler(antwort?.error ?? (err as Error)?.message ?? 'Export fehlgeschlagen.');
    } finally {
      setExportLaeuft(false);
    }
  };

  const { data: pruefbereiche = [] } = useQuery<Pruefbereich[]>({
    queryKey: ['pruefbereiche'],
    queryFn: () => pruefbereicheApi.liste(),
  });

  // Handle element parameter from QR code redirect
  useEffect(() => {
    const elementIdParam = searchParams.get('element');
    if (elementIdParam) {
      const elementId = parseInt(elementIdParam);
      if (!isNaN(elementId)) {
        // Fetch the element details and select it
        elementeApi.getByIdWithDetails(elementId).then((element) => {
          if (element) {
            setSelectedElement(element);
          }
        }).catch(log.error);
        // Clear the URL parameter
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  // Queries
  const { data: kunden } = useQuery({
    queryKey: ['kunden'],
    queryFn: kundenApi.getAll
  });

  const { data: standorte } = useQuery({
    queryKey: ['standorte', filters.kundeId],
    queryFn: () => standorteApi.getAll(filters.kundeId),
    enabled: true
  });

  const { data: gebaeude } = useQuery({
    queryKey: ['gebaeude', filters.standortId],
    queryFn: () => gebaeudeApi.getAll(filters.standortId),
    enabled: true
  });

  const { data: stockwerke } = useQuery({
    queryKey: ['stockwerke', filters.gebaeudeId],
    queryFn: () => stockwerkeApi.getAll(filters.gebaeudeId),
    enabled: true
  });

  const { data: elementsResponse, isLoading, refetch } = useQuery({
    queryKey: ['elemente-details', filters, page],
    queryFn: () => elementeApi.getAllWithDetails({
      kundeId: filters.kundeId,
      standortId: filters.standortId,
      gebaeudeId: filters.gebaeudeId,
      stockwerkId: filters.stockwerkId,
      typ: filters.typ,
      status: filters.status,
      search: filters.search || undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize
    })
  });

  const { data: elementMaengel } = useQuery({
    queryKey: ['element-maengel', selectedElement?.id],
    queryFn: () => maengelApi.getByElement(selectedElement!.id!),
    enabled: !!selectedElement?.id
  });

  const deleteMutation = useMutation({
    mutationFn: elementeApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['elemente-details'] });
      setSelectedElement(null);
    }
  });

  /**
   * "Vollstaendige Ansicht" eines Elements -- das Informationsblatt
   * (kompaktes A4-Blatt pro Tuer/Schott, siehe CLAUDE.md Berichtstypen).
   * Der Button hatte bisher kein onClick und tat schlicht nichts.
   */
  const handleVollstaendigeAnsicht = async (elementId: number) => {
    setErzeugeInformationsblatt(true);
    try {
      const blob = await reportsApi.downloadElementReport(elementId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `informationsblatt-element-${elementId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      log.error('Informationsblatt konnte nicht erzeugt werden:', err);
    } finally {
      setErzeugeInformationsblatt(false);
    }
  };

  const elements = elementsResponse?.data || [];
  const totalCount = elementsResponse?.total || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.kundeId) count++;
    if (filters.standortId) count++;
    if (filters.gebaeudeId) count++;
    if (filters.stockwerkId) count++;
    if (filters.typ) count++;
    if (filters.status) count++;
    return count;
  }, [filters]);

  const clearFilters = () => {
    setFilters({ search: filters.search });
    setPage(1);
  };

  const handleFilterChange = (key: keyof FilterState, value: any) => {
    const newFilters = { ...filters, [key]: value || undefined };

    // Reset dependent filters
    if (key === 'kundeId') {
      newFilters.standortId = undefined;
      newFilters.gebaeudeId = undefined;
      newFilters.stockwerkId = undefined;
    } else if (key === 'standortId') {
      newFilters.gebaeudeId = undefined;
      newFilters.stockwerkId = undefined;
    } else if (key === 'gebaeudeId') {
      newFilters.stockwerkId = undefined;
    }

    setFilters(newFilters);
    setPage(1);
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_LABELS[status] || { label: status, color: 'text-gray-700', bgColor: 'bg-gray-100' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold ${config.bgColor} ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getTypBadge = (typ: string) => {
    const config = TYP_LABELS[typ] || { label: typ, color: 'text-gray-700', bgColor: 'bg-gray-100' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold ${config.bgColor} ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="animate-in fade-in duration-500">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-secondary-900">Element-Übersicht</h1>
            <p className="text-sm text-secondary-500">
              {totalCount} {totalCount === 1 ? 'Element' : 'Elemente'} gefunden
            </p>
            {exportFehler && (
              <p className="text-sm text-red-600 mt-1">{exportFehler}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => refetch()}
              className="p-3 bg-white hover:bg-secondary-50 border border-secondary-200 rounded-2xl transition-all shadow-sm text-secondary-600"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={elementeExportieren}
              disabled={exportLaeuft}
              title="Elemente als Excel-Datei exportieren"
              className="p-3 bg-white hover:bg-secondary-50 border border-secondary-200 rounded-2xl transition-all shadow-sm text-secondary-600 disabled:opacity-50"
            >
              <Download size={20} className={exportLaeuft ? 'animate-pulse' : ''} />
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary-400 group-focus-within:text-primary-500 transition-colors" size={20} />
            <input
              type="text"
              placeholder="Elemente suchen (ID, Raum, Barcode...)"
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value });
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-secondary-200 rounded-xl focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all outline-none text-sm"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-6 py-2 flex items-center gap-2 rounded-2xl border transition-all font-bold ${
              showFilters || activeFiltersCount > 0
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'bg-white border-secondary-200 text-secondary-700 hover:bg-secondary-50'
            }`}
          >
            <Filter size={20} />
            Filter
            {activeFiltersCount > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-primary-500 text-white rounded-full text-xs">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 p-6 bg-white rounded-2xl border border-secondary-200 shadow-lg animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-secondary-900">Filter</h3>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Alle zurücksetzen
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-600 mb-2">Kunde</label>
                <select
                  value={filters.kundeId || ''}
                  onChange={(e) => handleFilterChange('kundeId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="input"
                >
                  <option value="">Alle Kunden</option>
                  {kunden?.map((k: any) => (
                    <option key={k.id} value={k.id}>{k.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-600 mb-2">Standort</label>
                <select
                  value={filters.standortId || ''}
                  onChange={(e) => handleFilterChange('standortId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="input"
                  disabled={!filters.kundeId}
                >
                  <option value="">Alle Standorte</option>
                  {standorte?.filter((s: any) => !filters.kundeId || s.kunde_id === filters.kundeId).map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-600 mb-2">Gebäude</label>
                <select
                  value={filters.gebaeudeId || ''}
                  onChange={(e) => handleFilterChange('gebaeudeId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="input"
                  disabled={!filters.standortId}
                >
                  <option value="">Alle Gebäude</option>
                  {gebaeude?.filter((g: any) => !filters.standortId || g.standort_id === filters.standortId).map((g: any) => (
                    <option key={g.id} value={g.id}>{g.bezeichnung}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-600 mb-2">Stockwerk</label>
                <select
                  value={filters.stockwerkId || ''}
                  onChange={(e) => handleFilterChange('stockwerkId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="input"
                  disabled={!filters.gebaeudeId}
                >
                  <option value="">Alle Stockwerke</option>
                  {stockwerke?.filter((sw: any) => !filters.gebaeudeId || sw.gebaeude_id === filters.gebaeudeId).map((sw: any) => (
                    <option key={sw.id} value={sw.id}>{sw.bezeichnung}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-600 mb-2">Typ</label>
                <select
                  value={filters.typ || ''}
                  onChange={(e) => handleFilterChange('typ', e.target.value || undefined)}
                  className="input"
                >
                  <option value="">Alle Typen</option>
                  <option value="TÜR">{TYP_LABELS['TÜR'].label}</option>
                  <option value="SCHOTT">{TYP_LABELS['SCHOTT'].label}</option>
                  <option value="FEUERLOESCHER">{TYP_LABELS['FEUERLOESCHER'].label}</option>
                  <option value="RWA">{TYP_LABELS['RWA'].label}</option>
                  <option value="BMA">{TYP_LABELS['BMA'].label}</option>
                  <option value="SPRINKLER">{TYP_LABELS['SPRINKLER'].label}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-600 mb-2">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                  className="input"
                >
                  <option value="">Alle Status</option>
                  <option value="i.O.">In Ordnung</option>
                  <option value="n.i.O.">Nicht in Ordnung</option>
                  <option value="NICHT_GEPRUEFT">Nicht geprüft</option>
                  <option value="IN_PRUEFUNG">In Prüfung</option>
                  <option value="GESPERRT">Gesperrt</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Element List */}
        <div className="lg:col-span-2">
          {isLoading ? (
            <div className="flex items-center justify-center p-10 bg-white rounded-2xl border border-secondary-200">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : elements.length === 0 ? (
            <div className="py-12 bg-white rounded-2xl border-2 border-dashed border-secondary-200 flex flex-col items-center justify-center text-secondary-400">
              <DoorOpen size={40} className="opacity-20 mb-4" />
              <p className="text-base font-medium">Keine Elemente gefunden</p>
              <p className="text-sm mt-2">Versuchen Sie andere Filterkriterien</p>
            </div>
          ) : (
            <div className="space-y-3">
              {elements.map((element: ElementWithDetails) => (
                <div
                  key={element.id}
                  onClick={() => setSelectedElement(element)}
                  className={`group p-5 bg-white rounded-2xl border transition-all cursor-pointer ${
                    selectedElement?.id === element.id
                      ? 'border-primary-500 shadow-lg shadow-primary-100 ring-4 ring-primary-50'
                      : 'border-secondary-200 hover:border-primary-200 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        element.typ === 'TÜR' ? 'bg-purple-100 text-purple-600' : 'bg-indigo-100 text-indigo-600'
                      }`}>
                        {element.typ === 'TÜR' ? <DoorOpen size={24} /> : <ShieldCheck size={24} />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-secondary-900 group-hover:text-primary-600 transition-colors">
                            {element.element_id_intern}
                          </h3>
                          {getTypBadge(element.typ)}
                          {getStatusBadge(element.status || 'NICHT_GEPRUEFT')}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-secondary-500">
                          {element.kunde_name && (
                            <span className="flex items-center gap-1">
                              <Users size={14} /> {element.kunde_name}
                            </span>
                          )}
                          {element.standort_name && (
                            <span className="flex items-center gap-1">
                              <MapPin size={14} /> {element.standort_name}
                            </span>
                          )}
                          {element.raum && (
                            <span className="flex items-center gap-1">
                              Raum: {element.raum}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {(element.maengel_count ?? 0) > 0 && (
                        <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-bold">
                          <AlertTriangle size={14} /> {element.maengel_count}
                        </span>
                      )}
                      {(element.fotos_count ?? 0) > 0 && (
                        <span className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">
                          <Camera size={14} /> {element.fotos_count}
                        </span>
                      )}
                      <ChevronRight size={20} className="text-secondary-300 group-hover:text-primary-500 transition-colors" />
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-sm text-secondary-500">
                    Seite {page} von {totalPages} ({totalCount} Elemente)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 bg-white border border-secondary-200 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50 transition-all"
                    >
                      Zurück
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-4 py-2 bg-white border border-secondary-200 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50 transition-all"
                    >
                      Weiter
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-1">
          {selectedElement ? (
            <div className="sticky top-6 p-6 bg-white rounded-2xl border border-secondary-200 shadow-xl animate-in slide-in-from-right-4 duration-300">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-secondary-900">Details</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      // Phase 6, Lücke 1: TÜR hat ein eigenes Formular
                      // (DoorCaptureWizard), alle anderen Bereiche --
                      // inklusive SCHOTT -- das generische.
                      const schluessel = schluesselFuerElementTyp(selectedElement.typ);
                      if (schluessel === 'TUER') {
                        setEditTuerElement(selectedElement);
                        return;
                      }
                      const bereich = pruefbereiche.find((b) => b.schluessel === schluessel);
                      if (bereich) {
                        setEditGenericBereich(bereich);
                      } else {
                        // Kein Katalogeintrag (mehr) fuer diesen Schluessel
                        // gefunden -- fachlich zu klaeren, statt ein
                        // Element ohne passendes Formular zu oeffnen.
                        setEditTuerElement(selectedElement);
                      }
                    }}
                    className="p-2 hover:bg-secondary-50 rounded-xl text-secondary-400 hover:text-primary-600 transition-colors"
                    title="Bearbeiten"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Element wirklich löschen?')) {
                        deleteMutation.mutate(selectedElement.id!);
                      }
                    }}
                    className="p-2 hover:bg-red-50 rounded-xl text-secondary-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                  <button
                    onClick={() => setSelectedElement(null)}
                    className="p-2 hover:bg-secondary-50 rounded-xl text-secondary-400 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Element Info */}
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  {(() => {
                    const typLabel = TYP_LABELS[selectedElement.typ];
                    const Icon = ELEMENT_TYP_ICONS[selectedElement.typ] || ShieldCheck;
                    return (
                      <div
                        className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                          typLabel ? `${typLabel.bgColor} ${typLabel.color}` : 'bg-indigo-100 text-indigo-600'
                        }`}
                      >
                        <Icon size={32} />
                      </div>
                    );
                  })()}
                  <div>
                    <h3 className="text-lg font-bold text-secondary-900">{selectedElement.element_id_intern}</h3>
                    <div className="flex gap-2 mt-1">
                      {getTypBadge(selectedElement.typ)}
                      {getStatusBadge(selectedElement.status || 'NICHT_GEPRUEFT')}
                    </div>
                  </div>
                </div>

                {/* Location Breadcrumb */}
                <div className="p-4 bg-secondary-50 rounded-2xl space-y-2">
                  {selectedElement.kunde_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Users size={16} className="text-secondary-400" />
                      <span className="font-medium text-secondary-700">{selectedElement.kunde_name}</span>
                    </div>
                  )}
                  {selectedElement.standort_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin size={16} className="text-secondary-400" />
                      <span className="text-secondary-600">{selectedElement.standort_name}</span>
                    </div>
                  )}
                  {selectedElement.gebaeude_bezeichnung && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 size={16} className="text-secondary-400" />
                      <span className="text-secondary-600">{selectedElement.gebaeude_bezeichnung}</span>
                    </div>
                  )}
                  {selectedElement.stockwerk_bezeichnung && (
                    <div className="flex items-center gap-2 text-sm">
                      <Layers size={16} className="text-secondary-400" />
                      <span className="text-secondary-600">{selectedElement.stockwerk_bezeichnung}</span>
                    </div>
                  )}
                </div>

                {/* Details Grid */}
                <div className="space-y-4">
                  <h4 className="font-bold text-secondary-700 border-b border-secondary-100 pb-2">Eigenschaften</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedElement.element_id_kunde && (
                      <div>
                        <span className="text-secondary-500">Kunden-ID</span>
                        <p className="font-medium text-secondary-900">{selectedElement.element_id_kunde}</p>
                      </div>
                    )}
                    {selectedElement.raum && (
                      <div>
                        <span className="text-secondary-500">Raum</span>
                        <p className="font-medium text-secondary-900">{selectedElement.raum}</p>
                      </div>
                    )}
                    {selectedElement.barcode && (
                      <div>
                        <span className="text-secondary-500">Barcode</span>
                        <p className="font-medium text-secondary-900">{selectedElement.barcode}</p>
                      </div>
                    )}
                    {selectedElement.pruef_intervall && (
                      <div>
                        <span className="text-secondary-500">Prüfintervall</span>
                        <p className="font-medium text-secondary-900">{selectedElement.pruef_intervall}</p>
                      </div>
                    )}
                    {selectedElement.letzte_pruefung && (
                      <div>
                        <span className="text-secondary-500">Letzte Prüfung</span>
                        <p className="font-medium text-secondary-900">
                          {new Date(selectedElement.letzte_pruefung).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    )}
                    {selectedElement.naechste_pruefung && (
                      <div>
                        <span className="text-secondary-500">Nächste Prüfung</span>
                        <p className="font-medium text-secondary-900">
                          {new Date(selectedElement.naechste_pruefung).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Fotos -- mehrere je Element, mit Markierungen */}
                {selectedElement?.id && (
                  <div className="space-y-3 border-t border-secondary-100 pt-4">
                    <FotoPanel elementId={selectedElement.id} />
                  </div>
                )}

                {/* QR Code Section */}
                <div className="space-y-3">
                  <button
                    onClick={() => setShowQRCode(!showQRCode)}
                    className="w-full flex items-center justify-between font-bold text-secondary-700 border-b border-secondary-100 pb-2"
                  >
                    <span className="flex items-center gap-2">
                      <QrCode size={16} className="text-primary-500" />
                      QR-Code
                    </span>
                    <ChevronRight size={16} className={`transition-transform ${showQRCode ? 'rotate-90' : ''}`} />
                  </button>
                  {showQRCode && selectedElement?.id && (
                    <div className="flex justify-center py-4 bg-secondary-50 rounded-xl">
                      <QRCodeDisplay
                        elementId={selectedElement.id}
                        elementName={selectedElement.element_id_intern}
                        size="md"
                      />
                    </div>
                  )}
                </div>

                {/* Mängel Section */}
                {elementMaengel && elementMaengel.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-bold text-secondary-700 border-b border-secondary-100 pb-2 flex items-center gap-2">
                      <AlertTriangle size={16} className="text-red-500" />
                      Mängel ({elementMaengel.length})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {elementMaengel.map((mangel: any) => (
                        <button
                          key={mangel.id}
                          type="button"
                          onClick={() => navigate(`/maengel?mangel=${mangel.id}`)}
                          className="w-full text-left p-3 bg-red-50 rounded-xl border border-red-100 hover:border-red-300 hover:shadow-sm transition-all"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                              mangel.schweregrad === 'KRITISCH' ? 'bg-red-600 text-white' :
                              mangel.schweregrad === 'WICHTIG' ? 'bg-amber-500 text-white' :
                              'bg-blue-500 text-white'
                            }`}>
                              {mangel.schweregrad}
                            </span>
                            <span className="text-xs text-secondary-500">{mangel.status}</span>
                          </div>
                          <p className="text-sm text-secondary-700 line-clamp-2">{mangel.beschreibung}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-4 border-t border-secondary-100 space-y-3">
                  <button
                    type="button"
                    disabled={erzeugeInformationsblatt}
                    onClick={() => selectedElement?.id && handleVollstaendigeAnsicht(selectedElement.id)}
                    className="w-full btn btn-primary py-2 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    <Eye size={18} />
                    {erzeugeInformationsblatt ? 'Wird erzeugt …' : 'Vollständige Ansicht'}
                  </button>
                  <button
                    type="button"
                    onClick={() => selectedElement?.id && navigate(`/maengel?element=${selectedElement.id}`)}
                    className="w-full btn btn-secondary py-2 flex items-center justify-center gap-2"
                  >
                    <AlertTriangle size={18} /> Mangel hinzufügen
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="sticky top-6 p-10 bg-white rounded-2xl border-2 border-dashed border-secondary-200 flex flex-col items-center justify-center text-secondary-400">
              <Eye size={36} className="opacity-20 mb-4" />
              <p className="text-sm font-medium">Kein Element ausgewählt</p>
              <p className="text-sm mt-1">Klicken Sie auf ein Element für Details</p>
            </div>
          )}
        </div>
      </div>

      {/* Bearbeiten-Overlay: TÜR (Phase 6, Lücke 1) */}
      {editTuerElement && (
        <DoorCaptureWizard
          stockwerkId={editTuerElement.stockwerk_id}
          stockwerkName={editTuerElement.stockwerk_bezeichnung || ''}
          editElement={editTuerElement}
          onClose={async () => {
            const id = editTuerElement.id;
            setEditTuerElement(null);
            await refetch();
            if (id) {
              try {
                const aktualisiert = await elementeApi.getByIdWithDetails(id);
                if (aktualisiert) setSelectedElement(aktualisiert);
              } catch {
                // Details bleiben auf dem letzten bekannten Stand.
              }
            }
          }}
        />
      )}

      {/* Bearbeiten-Overlay: Schott + neue Prüfbereiche (Phase 6, Lücke 1) */}
      {editGenericBereich && selectedElement && (
        <GenericElementCaptureWizard
          stockwerkId={selectedElement.stockwerk_id}
          stockwerkName={selectedElement.stockwerk_bezeichnung || ''}
          bereich={editGenericBereich}
          element={selectedElement}
          onClose={async () => {
            const id = selectedElement.id;
            setEditGenericBereich(null);
            await refetch();
            if (id) {
              try {
                const aktualisiert = await elementeApi.getByIdWithDetails(id);
                if (aktualisiert) setSelectedElement(aktualisiert);
              } catch {
                // Details bleiben auf dem letzten bekannten Stand.
              }
            }
          }}
        />
      )}
    </div>
  );
}
